import React, { useState, useCallback, useEffect } from 'react';
import ReactFlow, {
  Node,
  Edge,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  MiniMap,
  BackgroundVariant,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Save,
  Plus,
  Trash2,
  ArrowLeft,
  Circle,
  X,
  Clock
} from 'lucide-react';
import { User } from '../../types';
import { Workflow, WorkflowState, WorkflowTransition } from '../../types/workflow';
import { toast } from '../../utils/toast';

interface WorkflowDesignerProps {
  currentUser: User;
  workflowId?: string;
  onBack: () => void;
  onSave?: (workflow: Workflow) => void;
}

const WorkflowDesigner: React.FC<WorkflowDesignerProps> = ({
  currentUser,
  workflowId,
  onBack,
  onSave
}) => {
  const [workflow, setWorkflow] = useState<Partial<Workflow>>({
    name: '',
    description: '',
    applicableTaskTypes: ['all'],
    states: [],
    transitions: [],
    automationRules: [],
    isActive: false
  });

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveAsNewVersion, setSaveAsNewVersion] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  const TASK_TYPE_OPTIONS = ['all', 'server-issue', 'environment-setup', 'configuration', 'bug-investigation', 'infrastructure', 'maintenance', 'other'];
  const ROLE_OPTIONS = ['developer', 'tl', 'pm', 'devops', 'admin'];

  useEffect(() => {
    if (workflowId) {
      fetchWorkflow();
    }
  }, [workflowId]);

  const fetchWorkflow = async () => {
    try {
      const response = await fetch(`/api/workflows/${workflowId}`);
      if (response.ok) {
        const data = await response.json();
        setWorkflow(data);
        loadWorkflowToCanvas(data);
      }
    } catch (err) {
      console.error('Error fetching workflow:', err);
    }
  };

  const loadWorkflowToCanvas = (wf: Workflow) => {
    // Convert states to nodes
    const flowNodes: Node[] = wf.states.map((state) => ({
      id: state.id,
      type: 'default',
      position: state.position,
      data: {
        label: (
          <div className="px-3 py-2">
            <div className="font-semibold text-sm">{state.name}</div>
            {state.description && (
              <div className="text-xs text-gray-600 mt-1">{state.description}</div>
            )}
          </div>
        )
      },
      style: {
        background: state.color,
        color: '#fff',
        border: state.isInitial ? '3px solid #10b981' : state.isFinal ? '3px solid #ef4444' : '1px solid #ddd',
        borderRadius: '8px',
        padding: 0
      }
    }));

    // Convert transitions to edges
    const flowEdges: Edge[] = wf.transitions.map((transition) => ({
      id: transition.id,
      source: transition.fromState,
      target: transition.toState,
      label: transition.name,
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#6366f1', strokeWidth: 2 }
    }));

    setNodes(flowNodes);
    setEdges(flowEdges);
  };

  const onConnect = useCallback(
    (params: Connection) => {
      const newEdge = {
        ...params,
        id: `e-${Date.now()}`,
        type: 'smoothstep',
        animated: true,
        style: { stroke: '#6366f1', strokeWidth: 2 },
        label: 'New Transition'
      };
      setEdges((eds) => addEdge(newEdge as Edge, eds));
      
      // Add transition to workflow
      const newTransition: WorkflowTransition = {
        id: newEdge.id,
        name: 'New Transition',
        fromState: params.source!,
        toState: params.target!,
        description: ''
      };
      
      setWorkflow(prev => ({
        ...prev,
        transitions: [...(prev.transitions || []), newTransition]
      }));
    },
    [setEdges]
  );

  const handleAddState = () => {
    const newState: WorkflowState = {
      id: `state-${Date.now()}`,
      name: 'New State',
      description: '',
      color: '#3B82F6',
      position: { x: 250, y: 150 },
      isInitial: (workflow.states?.length || 0) === 0,
      isFinal: false
    };

    const newNode: Node = {
      id: newState.id,
      type: 'default',
      position: newState.position,
      data: {
        label: (
          <div className="px-3 py-2">
            <div className="font-semibold text-sm">{newState.name}</div>
          </div>
        )
      },
      style: {
        background: newState.color,
        color: '#fff',
        border: newState.isInitial ? '3px solid #10b981' : '1px solid #ddd',
        borderRadius: '8px',
        padding: 0
      }
    };

    setNodes((nds) => [...nds, newNode]);
    setWorkflow(prev => ({
      ...prev,
      states: [...(prev.states || []), newState]
    }));
  };

  const handleDeleteState = (stateId: string) => {
    if (!confirm('Delete this state? All connected transitions will also be removed.')) {
      return;
    }

    setNodes((nds) => nds.filter((n) => n.id !== stateId));
    setEdges((eds) => eds.filter((e) => e.source !== stateId && e.target !== stateId));
    
    setWorkflow(prev => ({
      ...prev,
      states: (prev.states || []).filter(s => s.id !== stateId),
      transitions: (prev.transitions || []).filter(t => 
        t.fromState !== stateId && t.toState !== stateId
      )
    }));
  };

  const handleSaveWorkflow = async () => {
    if (!workflow.name) {
      toast('Please enter a workflow name');
      return;
    }

    if ((workflow.states?.length || 0) === 0) {
      toast('Please add at least one state');
      return;
    }

    try {
      setSaving(true);

      // Update state positions from nodes
      const updatedStates = workflow.states?.map(state => {
        const node = nodes.find(n => n.id === state.id);
        if (node) {
          return { ...state, position: node.position };
        }
        return state;
      });

      const workflowData = {
        ...workflow,
        states: updatedStates,
        createdBy: {
          id: currentUser.id,
          name: currentUser.name,
          email: currentUser.email
        },
        // The backend already supports snapshotting the current version into
        // previousVersions before applying an update — it just required
        // createNewVersion/updatedBy in the body, which nothing here ever sent.
        ...(workflowId && saveAsNewVersion ? { createNewVersion: true, updatedBy: currentUser.name } : {})
      };

      const url = workflowId ? `/api/workflows/${workflowId}` : '/api/workflows';
      const method = workflowId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(workflowData)
      });

      if (response.ok) {
        const savedWorkflow = await response.json();
        toast('Workflow saved successfully!');
        if (onSave) {
          onSave(savedWorkflow);
        }
        onBack();
      } else {
        toast('Failed to save workflow');
      }
    } catch (err) {
      console.error('Error saving workflow:', err);
      toast('Error saving workflow');
    } finally {
      setSaving(false);
    }
  };

  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
    setSelectedEdge(null);
  }, []);

  const onEdgeClick = useCallback((_event: React.MouseEvent, edge: Edge) => {
    setSelectedEdge(edge);
    setSelectedNode(null);
  }, []);

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {workflowId ? 'Edit Workflow' : 'Create Workflow'}
              </h1>
              <p className="text-sm text-gray-600">
                Design your workflow by adding states and transitions
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {workflowId && (workflow.previousVersions?.length || 0) >= 0 && (
              <button
                onClick={() => setShowVersionHistory(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Clock className="w-4 h-4" />
                Version History (v{workflow.version || 1})
              </button>
            )}
            {workflowId && (
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={saveAsNewVersion}
                  onChange={(e) => setSaveAsNewVersion(e.target.checked)}
                />
                Save as new version
              </label>
            )}
            <button
              onClick={handleAddState}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add State
            </button>
            <button
              onClick={handleSaveWorkflow}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Workflow'}
            </button>
          </div>
        </div>

        {/* Workflow Details */}
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Workflow Name *
            </label>
            <input
              type="text"
              value={workflow.name || ''}
              onChange={(e) => setWorkflow({ ...workflow, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter workflow name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <input
              type="text"
              value={workflow.description || ''}
              onChange={(e) => setWorkflow({ ...workflow, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter description"
            />
          </div>
        </div>

        {/* Applicable Task Types — previously fixed at ['all'] with no way to change it */}
        <div className="mt-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">Applies To Task Types</label>
          <div className="flex flex-wrap gap-3">
            {TASK_TYPE_OPTIONS.map(type => (
              <label key={type} className="flex items-center gap-1.5 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={(workflow.applicableTaskTypes || []).includes(type)}
                  onChange={(e) => {
                    const current = workflow.applicableTaskTypes || [];
                    const next = e.target.checked
                      ? [...current, type]
                      : current.filter(t => t !== type);
                    setWorkflow({ ...workflow, applicableTaskTypes: next });
                  }}
                />
                {type}
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onEdgeClick={onEdgeClick}
          fitView
        >
          <Controls />
          <MiniMap />
          <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
        </ReactFlow>

        {/* Selected Node Panel */}
        {selectedNode && (
          <div className="absolute top-4 right-4 w-80 bg-white rounded-lg shadow-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Edit State</h3>
              <button
                onClick={() => setSelectedNode(null)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State Name
                </label>
                <input
                  type="text"
                  value={workflow.states?.find(s => s.id === selectedNode.id)?.name || ''}
                  onChange={(e) => {
                    const updatedStates = workflow.states?.map(s =>
                      s.id === selectedNode.id ? { ...s, name: e.target.value } : s
                    );
                    setWorkflow({ ...workflow, states: updatedStates });
                    
                    // Update node label
                    setNodes(nds => nds.map(n =>
                      n.id === selectedNode.id
                        ? {
                            ...n,
                            data: {
                              label: (
                                <div className="px-3 py-2">
                                  <div className="font-semibold text-sm">{e.target.value}</div>
                                </div>
                              )
                            }
                          }
                        : n
                    ));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <input
                  type="color"
                  value={workflow.states?.find(s => s.id === selectedNode.id)?.color || '#3B82F6'}
                  onChange={(e) => {
                    const updatedStates = workflow.states?.map(s =>
                      s.id === selectedNode.id ? { ...s, color: e.target.value } : s
                    );
                    setWorkflow({ ...workflow, states: updatedStates });
                    
                    // Update node color
                    setNodes(nds => nds.map(n =>
                      n.id === selectedNode.id
                        ? { ...n, style: { ...n.style, background: e.target.value } }
                        : n
                    ));
                  }}
                  className="w-full h-10 px-2 border border-gray-300 rounded-lg"
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={workflow.states?.find(s => s.id === selectedNode.id)?.isFinal || false}
                  onChange={(e) => {
                    const updatedStates = workflow.states?.map(s =>
                      s.id === selectedNode.id ? { ...s, isFinal: e.target.checked } : s
                    );
                    setWorkflow({ ...workflow, states: updatedStates });
                    setNodes(nds => nds.map(n =>
                      n.id === selectedNode.id
                        ? { ...n, style: { ...n.style, border: e.target.checked ? '3px solid #ef4444' : '1px solid #ddd' } }
                        : n
                    ));
                  }}
                />
                Final state
              </label>

              <button
                onClick={() => handleDeleteState(selectedNode.id)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete State
              </button>
            </div>
          </div>
        )}

        {/* Selected Edge Panel — transitions previously had no editing UI at all;
            clicking one just set selectedEdge with nothing rendered for it. */}
        {selectedEdge && (
          <div className="absolute top-4 right-4 w-80 bg-white rounded-lg shadow-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Edit Transition</h3>
              <button
                onClick={() => setSelectedEdge(null)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Transition Name</label>
                <input
                  type="text"
                  value={workflow.transitions?.find(t => t.id === selectedEdge.id)?.name || ''}
                  onChange={(e) => {
                    const updatedTransitions = workflow.transitions?.map(t =>
                      t.id === selectedEdge.id ? { ...t, name: e.target.value } : t
                    );
                    setWorkflow({ ...workflow, transitions: updatedTransitions });
                    setEdges(eds => eds.map(ed => ed.id === selectedEdge.id ? { ...ed, label: e.target.value } : ed));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={workflow.transitions?.find(t => t.id === selectedEdge.id)?.description || ''}
                  onChange={(e) => {
                    const updatedTransitions = workflow.transitions?.map(t =>
                      t.id === selectedEdge.id ? { ...t, description: e.target.value } : t
                    );
                    setWorkflow({ ...workflow, transitions: updatedTransitions });
                  }}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Required Role(s)</label>
                <div className="flex flex-wrap gap-2">
                  {ROLE_OPTIONS.map(role => {
                    const transition = workflow.transitions?.find(t => t.id === selectedEdge.id);
                    const checked = transition?.requiredRole?.includes(role) || false;
                    return (
                      <label key={role} className="flex items-center gap-1 text-xs text-gray-700">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const updatedTransitions = workflow.transitions?.map(t => {
                              if (t.id !== selectedEdge.id) return t;
                              const current = t.requiredRole || [];
                              const next = e.target.checked ? [...current, role] : current.filter(r => r !== role);
                              return { ...t, requiredRole: next };
                            });
                            setWorkflow({ ...workflow, transitions: updatedTransitions });
                          }}
                        />
                        {role}
                      </label>
                    );
                  })}
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={workflow.transitions?.find(t => t.id === selectedEdge.id)?.requiresApproval || false}
                  onChange={(e) => {
                    const updatedTransitions = workflow.transitions?.map(t =>
                      t.id === selectedEdge.id ? { ...t, requiresApproval: e.target.checked } : t
                    );
                    setWorkflow({ ...workflow, transitions: updatedTransitions });
                  }}
                />
                Requires approval
              </label>

              <button
                onClick={() => {
                  if (!confirm('Delete this transition?')) return;
                  setEdges(eds => eds.filter(e => e.id !== selectedEdge.id));
                  setWorkflow(prev => ({
                    ...prev,
                    transitions: (prev.transitions || []).filter(t => t.id !== selectedEdge.id)
                  }));
                  setSelectedEdge(null);
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete Transition
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Version History Modal */}
      {showVersionHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Clock className="w-5 h-5 text-gray-600" />
                Version History
              </h3>
              <button onClick={() => setShowVersionHistory(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <span className="font-semibold">v{workflow.version || 1}</span> — current version
              </div>
              {(workflow.previousVersions || []).length === 0 ? (
                <p className="text-sm text-gray-500">No earlier versions saved yet.</p>
              ) : (
                [...(workflow.previousVersions || [])].reverse().map((pv, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                    <span className="font-semibold">v{pv.version}</span>
                    <span className="text-gray-500"> — saved {new Date(pv.updatedAt).toLocaleString()}</span>
                    {pv.updatedBy && <span className="text-gray-500"> by {pv.updatedBy}</span>}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stats Footer */}
      <div className="bg-white border-t border-gray-200 px-6 py-3">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center gap-6">
            <span>States: {workflow.states?.length || 0}</span>
            <span>Transitions: {workflow.transitions?.length || 0}</span>
            <span>Rules: {workflow.automationRules?.length || 0}</span>
          </div>
          <div className="flex items-center gap-2">
            <Circle className="w-3 h-3 fill-green-500 text-green-500" />
            <span>Initial State</span>
            <Circle className="w-3 h-3 fill-red-500 text-red-500 ml-4" />
            <span>Final State</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowDesigner;
